import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Shield, Star, Zap } from 'lucide-react'
import { useDevice } from '../../hooks/useDevice'

const GetStarted: React.FC = () => {
  const navigate = useNavigate()
  const { isMobile, isTablet, isDesktop, isAndroid } = useDevice()

  return (
    <div className="fixed inset-0 overflow-hidden bg-black">
      {/* Background Image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/pictorials/city.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.65)',
        }}
      />

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/90" />
      <div className="absolute inset-0 bg-gradient-to-t from-safaricab-gold/5 to-transparent" />

      {/* Content Container */}
      <div className="relative z-10 flex flex-col h-full w-full max-w-lg mx-auto" style={{ paddingTop: 'max(var(--sai-top), 14px)' }}>
        {/* Top Logo Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className={`flex flex-col items-center ${
            isDesktop ? 'pt-20 pb-8' : 
            isTablet ? 'pt-16 pb-6' : 
            'pt-12 pb-4'
          } px-4`}
        >
          {/* Logo */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8, type: 'spring' }}
            className="relative mb-3 sm:mb-4"
          >
            <div className="absolute inset-0 bg-safaricab-gold/20 rounded-full blur-3xl" />
            <img
              src="/logo-lion.png"
              alt="SafariCab"
              className={`relative object-contain drop-shadow-2xl ${
                isDesktop ? 'w-32 h-32' :
                isTablet ? 'w-28 h-28' :
                'w-24 h-24'
              }`}
            />
          </motion.div>

          {/* Brand Name */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className={`font-extrabold text-white tracking-widest font-heading ${
              isDesktop ? 'text-6xl' :
              isTablet ? 'text-5xl' :
              'text-4xl'
            }`}
            style={{ textShadow: '0 0 40px rgba(245,166,35,0.3)' }}
          >
            SAFARICAB
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className={`text-safaricab-gold tracking-[0.3em] sm:tracking-[0.5em] uppercase mt-2 font-light ${
              isDesktop ? 'text-base' :
              isTablet ? 'text-sm' :
              'text-xs'
            }`}
          >
            Online Taxi Service
          </motion.p>
        </motion.div>

        {/* Features Row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className={`flex justify-center px-4 sm:px-8 mb-6 sm:mb-8 ${
            isDesktop ? 'gap-12' :
            isTablet ? 'gap-10' :
            'gap-6'
          }`}
        >
          {[
            { icon: Shield, label: 'Safe' },
            { icon: Zap, label: 'Fast' },
            { icon: Star, label: 'Top Rated' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-1.5 sm:gap-2">
              <div className={`rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center ${
                isDesktop ? 'w-16 h-16' :
                isTablet ? 'w-14 h-14' :
                'w-12 h-12'
              }`}>
                <Icon className={`text-safaricab-gold ${
                  isDesktop ? 'w-7 h-7' :
                  isTablet ? 'w-6 h-6' :
                  'w-5 h-5'
                }`} />
              </div>
              <span className={`text-white/70 font-medium ${
                isDesktop ? 'text-sm' : 'text-xs'
              }`}>{label}</span>
            </div>
          ))}
        </motion.div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="px-4 sm:px-6 space-y-3 sm:space-y-4"
          style={{ paddingBottom: 'calc(var(--sai-bottom) + 2.25rem)' }}
        >
          {/* Get Started Button - matches driver app */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/signup')}
            className="w-full bg-safaricab-gold hover:bg-yellow-500 text-black font-bold py-5 rounded-2xl text-lg flex items-center justify-center gap-3 shadow-2xl shadow-safaricab-gold/30"
          >
            <img src="/icons/getstarted.png" alt="" className="h-6 w-auto" />
            Get Started
            <ArrowRight className="w-5 h-5" />
          </motion.button>

          {/* Login Button - matches driver app */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/login')}
            className="w-full bg-white/5 backdrop-blur-sm border-2 border-white/20 hover:border-white/40 text-white font-semibold py-5 rounded-2xl text-lg"
          >
            Login
          </motion.button>

          {/* Bottom Text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 0.8 }}
            className={`text-center text-white/40 font-light tracking-wide ${
              isDesktop ? 'text-base pt-6' :
              isTablet ? 'text-sm pt-4' :
              'text-xs pt-3'
            }`}
          >
            Ride Safe, Ride Smart
          </motion.p>
        </motion.div>
      </div>
    </div>
  )
}

export default GetStarted
